import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { hoursToMilliseconds } from 'date-fns';
import { getTrial } from 'sync/trial-service';
import { TrialContext } from 'sync/trial-service/trial-service.types';
import { queryClient, ReactQueryKeys } from 'utils/react-query';

type UseTrialsProps = {
  trialContexts: TrialContext[];
};

/**
 * Specifies how long response is cached in localStorage.
 */
const TRIALS_CACHE_TIME = hoursToMilliseconds(10);

export const useTrials = ({ trialContexts }: UseTrialsProps) => {
  const queries = useQueries({
    queries: trialContexts.map((trialParams) => ({
      queryKey: ReactQueryKeys.TRIAL(trialParams.organizationId),
      queryFn: () => getTrial(trialParams),
      keepPreviousData: false,
      cacheTime: TRIALS_CACHE_TIME,
      enabled: !!trialParams.organizationId,
      refetchOnMount: false,
    })),
  });

  const trialsByOrganizationId = useMemo(() => {
    return Object.fromEntries(
      trialContexts
        .filter((_, index) => queries[index].data)
        .map((trial, index) => {
          return [trial.organizationId, queries[index].data!] as const;
        })
    );
  }, [queries, trialContexts]);

  const invalidateTrials = () => {
    queryClient.invalidateQueries(trialContexts.map((trialParams) => ReactQueryKeys.TRIAL(trialParams.organizationId)));
  };

  return {
    trials: queries.map((query) => query.data),
    isLoading: queries.some((query) => query.isLoading),
    trialsByOrganizationId,
    invalidateTrials,
  };
};
